import { ReactElement } from "react";
import { useDependency } from "../../../../contexts/DependencyContext";
import { useTimeMachine } from "../../../../contexts/TimeMachineContext"
import { useRecurrentAsyncCall } from "../../../../hooks/UseAsyncCall";
import { IIndicatorResult } from "../../../../services/http/indicatorlist/models/IIndicatorResult";
import { GetIndicatorsParameters } from "../../../../services/http/indicatorlist/models/GetIndicatorsParameters";
import { IndicatorListService } from "../../../../services/http/indicatorlist/IndicatorListService";
import { DateService } from "../../../../services/types/DateService";
import Spinner from "../../Spinner";
import NoData from "../../NoData";
import Issue from "./Issue";
import { className } from "../../../../shared/helpers";
import { useTooltip } from "../../../../hooks/UseTooltip";
import styles from "./CurrentIssues.module.scss";

interface CurrentIssuesProps
{
    componentId: number;
    widgetId: number;
}

async function loadData(service: IndicatorListService, componentId: number, date: Date): Promise<IIndicatorResult[]>
{
    const parameters = new GetIndicatorsParameters();
    parameters.componentId = componentId;
    parameters.date = date;
    return await service.getFailingIndicators(parameters);
}

const CurrentIssues = (props: CurrentIssuesProps): ReactElement =>
{
    const service = useDependency(IndicatorListService);
    const dateService = useDependency(DateService);
    const timeMachineDate = useTimeMachine();
    const { setupTooltip, clearTooltip, Tooltip } = useTooltip();
    const result = useRecurrentAsyncCall(parseInt(process.env.REACT_APP_CHART_REFRESH_RATE ?? "300000"), loadData, service, props.componentId, timeMachineDate);
    const hasData = !result.loading && result.content && result.content.length > 0;

    if (result.loading) return <Spinner caption="Loading widget..." />;

    return hasData
        ? (
            <>
                <div
                    {...className(styles.currentIssues, "scroll-container")}
                    onMouseLeave={clearTooltip}
                >
                    {result.content && result.content.map((x, i) => (
                        <Issue
                            key={x.indicatorId}
                            firstIssue={i === 0}
                            component={x}
                            setupTooltip={setupTooltip}
                            widgetId={props.widgetId}
                        />
                    ))}
                </div>

                <Tooltip />
            </>
        )
        : <NoData message={`No current issues at ${dateService.format(timeMachineDate ?? new Date(), 'MM/dd/yyyy HH:mm:ss')}`} />;
}

export default CurrentIssues;